import { defineStore } from 'pinia'
import { computed, onMounted, ref, watch } from 'vue'

import { UserDependentStore } from './UserDependentStore'
import { PropertyValueDTO, useClassificationStore } from './classification';

import { IMaterialDTO } from '@/types/material';
import { DbConstants } from '@/const';
import { buildMaterialLocalization, DEBUG } from '@/util';
import { ISparePartFilterGridResultState, ISparePartFilterTextSearchState, ISparePartTypeFilterTreeState } from '@/types/sparePartFilter';

/** 
 * 
 *  Store for the current spare part filter state.
 * 
 */
export const useSparePartFilterStore = defineStore('sparePartFilterStore', () => {
    const classificationStore = useClassificationStore()
    const { materialTextFor } = buildMaterialLocalization()

    /********************************************************************************************************************
     PRIVATE
     ********************************************************************************************************************/
    const defaultTypeTreeStateFactory = () => ({
        expanded: {
            ids: ['all'],
            idField: 'id'
        },

        selected: {
            ids: ['all'],
            idField: 'id'
        },

        selectedTypes: allDescendantsOfType('all')
    }) as ISparePartTypeFilterTreeState

    const defaultGridStateFactory = () => ({
        selectedId: null,
        numberOfLoadedParts: 50
    }) as ISparePartFilterGridResultState

    const defaultSearchStateFactory = () => ({
        text: null,
        searchFunction: null
    }) as ISparePartFilterTextSearchState

    /********************************************************************************************************************
     STATE
     ********************************************************************************************************************/
    const _sourceData = ref<IMaterialDTO[] | null>(null)
    const _spareParts = computed(() => _sourceData.value ?? []) 

    const _typeTreeState = ref<ISparePartTypeFilterTreeState>(defaultTypeTreeStateFactory())
    const _gridState = ref<ISparePartFilterGridResultState>(defaultGridStateFactory())
    const _searchState = ref<ISparePartFilterTextSearchState>(defaultSearchStateFactory())

    const _selectedFunctions = ref<string[]>([])

    const state = {
        _sourceData,
        _spareParts,
        _typeTreeState,
        _gridState,
        _searchState,
        _selectedFunctions        
    }

    /********************************************************************************************************************
     GETTERS (STATE)
     ********************************************************************************************************************/
    const hasLoaded = computed(() => _sourceData.value != null)

    const typeTreeState = computed(() => _typeTreeState.value)
    const searchState = computed(() => _searchState.value)
    const gridState = computed(() => _gridState.value)

    const selectedTypes = computed(() => _typeTreeState.value.selectedTypes)
    const selectedFunctions = computed(() => _selectedFunctions.value)
    const currentSearchText = computed(() => _searchState.value?.text ?? null)

    const filterIsActive = computed(() =>
        (typeTreeState.value.selected.ids.length == 1 && typeTreeState.value.selected.ids[0] != 'all') ||
        (selectedFunctions.value.length > 0) ||
        (searchState.value.searchFunction != null)
    )

    /********************************************************************************************************************
     GETTERS (FILTER RESULTS)
     ********************************************************************************************************************/
    /** Returns all spare parts, with only search text filtering applied */
    const sparePartsFilteredBySearchText = computed(() =>
        getSparePartsFilteredBySearchText(_spareParts.value, _searchState.value)
    )

    /** Returns all spare parts, with search text and spare part type filtering applied */
    const sparePartsFilteredByTypeAndSearch = computed(() =>
        getSparePartsFilteredByType(sparePartsFilteredBySearchText.value, selectedTypes.value)
    )

    /** Returns all spare parts, with search text and spare part function filtering applied */
    const sparePartsFilteredByFunctionAndSearch = computed(() =>
        getSparePartsFilteredByFunction(sparePartsFilteredBySearchText.value, _selectedFunctions.value)
    )
    /** Returns all spare parts, with all 3 filters applied */
    const filteredSpareParts = computed(() =>
        getSparePartsFilteredByFunction(sparePartsFilteredByTypeAndSearch.value, _selectedFunctions.value)
            .sortBy(p => materialTextFor(p))
    )

    /** Returns a map of result counts statistics (indexed by spare part type), when search text and spare part function filtering is applied */
    const sparePartTypeStats = computed(() => getSparePartTypeStats(sparePartsFilteredByFunctionAndSearch.value))

    /** Returns a map of result counts statistics (indexed by spare part function), when search text and spare part type filtering is applied */
    const sparePartFunctionStats = computed(() => getSparePartFunctionStats(sparePartsFilteredByTypeAndSearch.value))

    /********************************************************************************************************************
     GETTERS (PROPERTY DATA)
     ********************************************************************************************************************/
    /** Returns all property values for property 'Sparepart Model' */
    const allSparepartModelPropertyValues = computed(() =>
        classificationStore.getPropertyValueDTOs(DbConstants.sparePartPropertyNames.sparePartModel))

    /** Returns all property values for property 'Sparepart Type' */
    const allSparepartTypePropertyValues = computed(() =>
        classificationStore.getPropertyValueDTOs(DbConstants.sparePartPropertyNames.sparePartType))

    /** Returns all property values for property 'Sparepart Function' */
    const allSparePartFunctionPropertyValues = computed(() =>
        classificationStore.getPropertyValueDTOs(DbConstants.sparePartPropertyNames.sparePartFunction))

    /** Returns all property values for property 'Sparepart Function' that have non-zero result count initially (when no filter is set) */
    const allNonZeroResultSparePartFunctionPropertyValues = computed(() => getNonZeroSparePartFunctionPropertyValues(allSparePartFunctionPropertyValues.value))

    const getters = {
        hasLoaded,
        selectedTypes,
        selectedFunctions,
        currentSearchText,
        filterIsActive,
        typeTreeState,
        searchState,
        gridState,
        sparePartsFilteredBySearchText,
        sparePartsFilteredByTypeAndSearch,
        sparePartsFilteredByFunctionAndSearch,
        filteredSpareParts,
        sparePartTypeStats,
        sparePartFunctionStats,
        allSparepartModelPropertyValues,
        allSparepartTypePropertyValues,
        allSparePartFunctionPropertyValues,
        allNonZeroResultSparePartFunctionPropertyValues
    }

    /********************************************************************************************************************
     ACTIONS
     ********************************************************************************************************************/
    function setSpareParts(parts: IMaterialDTO[] | null) {
        if (parts == null) {
            DEBUG('sparePartFilterStore: parts cleared')
        }
        else {
            DEBUG(`sparePartFilterStore: ${parts.length} parts`)
        }
        _sourceData.value = parts
    }

    function setSelectedType(newSelectedType: string) {
        _typeTreeState.value.selected.ids = [newSelectedType]
        _typeTreeState.value.selectedTypes = allDescendantsOfType(newSelectedType)

        // auto-expand relevant nodes
        _typeTreeState.value.expanded.ids = autoExpandTypeTree(newSelectedType)
    }

    function setSelectedFunctons(selectedfunctions: string[]) {
        _selectedFunctions.value = [ ...selectedfunctions ]
    }

    function removeSelectedFunction(funcToRemove: string) {
        _selectedFunctions.value = _selectedFunctions.value.filter(func => func != funcToRemove)
    }

    function setSearchState(newState: ISparePartFilterTextSearchState) {
        _searchState.value = newState
    }

    function setGridState(newState: ISparePartFilterGridResultState) {
        _gridState.value = newState
    }

    function resetTypeTreeState() {
        _typeTreeState.value = defaultTypeTreeStateFactory()
    }

    function resetSearchState() {
        _searchState.value = defaultSearchStateFactory()
    }

    function resetGridState() {
        _gridState.value = defaultGridStateFactory()
    }

    function resetFilterState() {
        resetTypeTreeState()
        resetSearchState()
        resetGridState()
        setSelectedFunctons([])
    }

    function resetAll() {
        _sourceData.value = []        
        _selectedFunctions.value = []

        resetTypeTreeState()
        resetSearchState()
        resetGridState()
    }

    const actions = {
        setSpareParts,
        setSelectedType,
        setSelectedFunctons,
        removeSelectedFunction,
        setSearchState,
        setGridState,
        resetTypeTreeState,
        resetSearchState,
        resetGridState,
        resetFilterState,
        resetAll
    }

    /********************************************************************************************************************
     PRIVATE
     ********************************************************************************************************************/

    /** Returns all the descendants of the specified spare part type */
    function allDescendantsOfType(sparePartType: string): string[] {
        let result: string[] = [ ]

        const allTypeProps = allSparepartTypePropertyValues?.value

        if (allTypeProps) {

            const rootProp = allTypeProps.find(p => p.propertyValue == sparePartType)

            if (rootProp != null) {
                result = getAllDescendantsOfType(rootProp, allTypeProps).map(p => p.propertyValue)
            }
        }

        return result
    }

    function getAllDescendantsOfType(root: PropertyValueDTO, allTypeProps: PropertyValueDTO[]): PropertyValueDTO[] {
        const childProps = allTypeProps.filter(p => p.parentPropertyValue == root.propertyValue)

        const result = [root]

        if (childProps.length) {
            result.push(...childProps.flatMap(child => getAllDescendantsOfType(child, allTypeProps)))
        }

        return result
    }

    /** Type filtering: Returns the subset of the specified spare parts that have one of the selected spare part types */
    function getSparePartsFilteredByType(parts: IMaterialDTO[], selectedTypes: string[]): IMaterialDTO[] {
        let result = [...parts]

        if (selectedTypes.length) {
            result = result.filter(p => selectedTypes.includes(p.sparePartType ?? ''))
        }
        return result
    }

    /** Function filtering: Returns the subset of the specified spare parts that are used in one of the selected functions */
    function getSparePartsFilteredByFunction(parts: IMaterialDTO[], selectedFunctions: string[]): IMaterialDTO[] {
        let result = [...parts]

        if (selectedFunctions.length) {
            result = result.filter(p => p.functionsUsingSparePart.some(f => selectedFunctions.includes(f)))
        }
        return result
    }

    /** Text search filtering: Returns the subset of the specified spare parts that match the current search state */
    function getSparePartsFilteredBySearchText(parts: IMaterialDTO[], searchState: ISparePartFilterTextSearchState | null): IMaterialDTO[] {
        let result = [...parts]

        const searchFunction = searchState?.searchFunction

        if (searchFunction != null) {
            result = result.filter(p => searchFunction(p))
        }
        return result
    }

    /** Returns a map that summarizes the number of spare parts of each spare part type */
    function getSparePartTypeStats(parts: IMaterialDTO[]): Map<string, number> {
        const result = parts.reduce<Map<string, number>>((acc, part) => {
            const sparePartType = part.sparePartType
            if (sparePartType != null) {
                const existingCount = acc.get(sparePartType)
                acc.set(sparePartType, (existingCount ?? 0) + 1)
            }
            return acc
        },
            new Map<string, number>)
        return result
    }

    /** 
     * Returns a map that summarizes the number of spare parts of each spare part function
     * (taking into account, that each spare part can be allocated to several functions,
     *  which means that a simple bottom-up accumulation is not possible)
     */
    function getSparePartFunctionStats(parts: IMaterialDTO[]): Map<string, number> {
        // First retrieve lists of material numbers grouped by function
        const map = getMapForFunctionToMaterials(parts)

        // Now recursively build map of matching counts (taking into account, that each material might be part of several functions)
        const sparePartFunctionPropertyValues = allSparePartFunctionPropertyValues.value
        const result = new Map<string, number>
        getSparePartFunctionStatsRecursively(null, map, result, sparePartFunctionPropertyValues)
        return result
    }

    function getMapForFunctionToMaterials(filteredParts: IMaterialDTO[]): Map<string, number[]> {
        const result = filteredParts.reduce<Map<string, number[]>>((acc, part) => {
            const sparePartFunctions = part.functionsUsingSparePart
            sparePartFunctions?.forEach(func => {
                const materialList = acc.get(func)
                if (materialList) {
                    materialList.push(part.materialNo)
                }
                else {
                    acc.set(func, [part.materialNo])
                }
            })
            return acc
        },
            new Map<string, number[]>)
        return result
    }

    function getSparePartFunctionStatsRecursively(parent: string | null,
        functionToMaterialsMap: Map<string, number[]>,
        resultMap: Map<string, number>,
        allSparePartFunctions: PropertyValueDTO[]): number[] {
        const currentFunctions = allSparePartFunctions.filter(pv => pv.parentPropertyValue == parent)
        const allMaterialNumbersOnThisLevel: number[] = []
        currentFunctions.forEach(func => {
            const funcValue = func.propertyValue
            const materialNumbersFromChildNodes = getSparePartFunctionStatsRecursively(funcValue, functionToMaterialsMap, resultMap, allSparePartFunctions)
            const materialNumbersMatchingNode = functionToMaterialsMap.get(funcValue) ?? []
            const combined = [...materialNumbersFromChildNodes, ...materialNumbersMatchingNode].distinct()
            allMaterialNumbersOnThisLevel.push(...combined)
            resultMap.set(func.propertyValue, (resultMap.get(funcValue) ?? 0) + combined.length)
        })
        // This is the important part, ensuring that each material number only counts once, even if it is part of multiple functions
        const result = allMaterialNumbersOnThisLevel.distinct()
        return result
    }

    function getNonZeroSparePartFunctionPropertyValues(propValues: PropertyValueDTO[]): PropertyValueDTO[] {
        // skip functions that have no resultCount initially
        const initialFunctionStats = getSparePartFunctionStats(_spareParts.value)
        const result = propValues.filter(pv => (initialFunctionStats.get(pv.propertyValue) ?? 0) > 0)
        return result
    }

    /** Determines the nodes to expand in the type tree according to the current selection */
    function autoExpandTypeTree(selectedType: string): string[] {
        const result = [ 'all' ]

        if (selectedType != 'all') {
            const propValues = classificationStore.getHierarchy(DbConstants.sparePartPropertyNames.sparePartType, selectedType)
            result.push(...propValues.map(p => p.propertyValue))
        }

        return result
    }

    /********************************************************************************************************************
     WATCHERS
     ********************************************************************************************************************/
    // reset number of max loaded materials, whenever the user changes the filter
    watch(() => _typeTreeState.value.selectedTypes, () => {
        resetGridState() 
    })
    watch(_selectedFunctions, () => {
        resetGridState()
    })
    watch(() => _searchState.value.text, () => {
        resetGridState()
    })

    /********************************************************************************************************************
     INIT
     ********************************************************************************************************************/
    onMounted(() => resetTypeTreeState())

    /********************************************************************************************************************
     MISC
     ********************************************************************************************************************/
    // Setup dependency on user store
    new UserDependentStore(() => resetAll(), () => resetAll())

    return { ...state, ...getters, ...actions  }
})